import React from 'react';

import type { IntlShape } from 'react-intl';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid';

// eslint-disable-next-line import/no-extraneous-dependencies
import type { ADFEntity, ADFEntityMark } from '@atlaskit/adf-utils/types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { type ContextualToolbar } from '@atlaskit/editor-common/extensions';
import ChartBar from '@atlaskit/icon/core/chart-bar';

import { i18n } from '../messages';
import type { ParseNumber } from '../types';

import { fireToolbarButtonAnalyticsEvent } from './analytics';
import { buildChartADF } from './chartADF';

type TableToolbar = {
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	featureFlags?: { [flag: string]: any };
	intl: IntlShape;
	onInsertButton?: (sourceNode: any) => void;
	parseNumber: ParseNumber;
};

function insertChartIcon() {
	return (() => <ChartBar label="" spacing="spacious" />) as any;
}

export const tableToolbar = ({
	intl,
	parseNumber,
	createAnalyticsEvent,
	onInsertButton,
}: TableToolbar): ContextualToolbar => {
	return {
		context: {
			type: 'node',
			nodeType: 'table',
		},
		toolbarItems: (adf: any, _api: any) => {
			// CEMS-2157: waiting on CEMS-2226 and CEMS-2227
			// const disabled = !tableHasNumbers(adf, parseNumber);
			const disabled = false;

			return [
				{
					key: 'insert-chart',
					display: 'icon' as const,
					disabled,
					icon: insertChartIcon,
					label: intl.formatMessage(i18n.insertChartTooltip),
					tooltip: intl.formatMessage(i18n.insertChartTooltip),

					action: async (adf: any, api: any) => {
						const localId: string = adf.attrs?.localId || '';
						let sourceId = localId;

						const existingFragmentMark = adf.marks?.find(
							(m: ADFEntityMark) => m.type === 'fragment',
						);

						// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
						sourceId = existingFragmentMark?.attrs?.localId || uuid();

						if (!existingFragmentMark) {
							api.doc.update(localId, ({ attrs, marks }: Pick<ADFEntity, 'attrs' | 'marks'>) => ({
								attrs,
								marks: (marks ?? []).concat({
									type: 'fragment',
									attrs: { localId: sourceId },
								}),
							}));
						}

						const chartADF = buildChartADF({
							adf,
							intl,
							parseNumber,
							sourceId,
						});
						api.doc.insertAfter(localId, chartADF, {
							allowSelectionToNewNode: true,
						});
						fireToolbarButtonAnalyticsEvent(adf, createAnalyticsEvent);
						if (onInsertButton) {
							onInsertButton(adf);
						}
					},
				},
			];
		},
	};
};
